import React, { Fragment, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { makeStyles } from '@material-ui/core/styles';

import { ButtonToolbar, Button } from 'reactstrap';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import DateField from '../../../shared/components/form/DateField';
import Select from '../../../shared/components/form/Select';
import { EMPTY_STRING, PERIOD_TYPE } from '../../../shared/utils/constants';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';

const Filter = (props) => {
  const {
    t,
    students,
    employees,
    classes,
    filterClick,
    initialize,
    lastFilter,
    memoBox,
  } = props;

  const [communicationFilter, setCommunicationFilter] = useState({
    initialPeriod: new Date(),
    finalPeriod: new Date(),
    classes: [],
    students: [],
    employees: [],
    periodType: PERIOD_TYPE.DELIVERY,
    communicationType: { value: memoBox },
    onlySent: false,
  });

  const useStyles = makeStyles({
    root: {
      color: '#646777',
      fontSize: '13px',
    },
    label: {
      fontSize: '13px',
    },
  });

  const classesStyle = useStyles();

  const communicationFilterChange = (field, value) => {
    const updatedCommunicationFilter = { ...communicationFilter };
    updatedCommunicationFilter[field] = value;
    setCommunicationFilter(updatedCommunicationFilter);
  };

  const communicationFilterInputChange = (evt) => {
    if (evt) {
      const updatedCommunicationFilter = { ...communicationFilter };
      updatedCommunicationFilter[evt.target.name] = evt.target.value;
      setCommunicationFilter(updatedCommunicationFilter);
    }
  };

  const communicationFilterCheckChange = (evt) => {
    if (evt) {
      const updatedCommunicationFilter = { ...communicationFilter };
      updatedCommunicationFilter[evt.target.name] = evt.target.checked;
      setCommunicationFilter(updatedCommunicationFilter);
    }
  };

  useEffect(() => {
    if (lastFilter) {
      let updatedCommunicationFilter = { ...lastFilter };
      if (lastFilter.periodType === EMPTY_STRING) {
        updatedCommunicationFilter = {
          ...updatedCommunicationFilter,
          periodType: PERIOD_TYPE.DELIVERY,
        };
      }
      setCommunicationFilter(updatedCommunicationFilter);
      initialize(updatedCommunicationFilter);
    } else {
      initialize(communicationFilter);
    }
  }, []);

  return (
    <Fragment>
      <form className="form">
        <div className="form__form-group">
          <div className="form__form-group-field">
            <RadioGroup
              row
              value={communicationFilter.periodType}
              name="periodType"
              onChange={(evt) => communicationFilterInputChange(evt)}
            >
              <FormControlLabel
                value={PERIOD_TYPE.DELIVERY}
                classes={{
                  root: classesStyle.root,
                  label: classesStyle.label,
                }}
                control={<Radio />}
                disabled={
                  communicationFilter.periodType === PERIOD_TYPE.SCHEDULED &&
                  communicationFilter.communicationType.value === EMPTY_STRING
                }
                label={t('general.delivery-date')}
                labelPlacement="end"
              />
              <FormControlLabel
                value={PERIOD_TYPE.SCHEDULED}
                classes={{
                  root: classesStyle.root,
                  label: classesStyle.label,
                }}
                control={<Radio />}
                label={t('general.scheduled-date')}
                labelPlacement="end"
              />
            </RadioGroup>
          </div>
        </div>

        <div className="form__form-group--2_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.initial_date')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="initialPeriod"
                component={DateField}
                onChange={(value) =>
                  communicationFilterChange('initialPeriod', value)
                }
                placeholder={t('general.initial_date')}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.final_date')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="finalPeriod"
                component={DateField}
                minDate={communicationFilter.initialPeriod}
                onChange={(value) =>
                  communicationFilterChange('finalPeriod', value)
                }
                placeholder={t('general.final_date')}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.classes-names')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="classes"
              type="text"
              closeMenuOnSelect={false}
              component={Select}
              isMulti
              options={classes}
              onChange={(values) =>
                communicationFilterChange('classes', values)
              }
              placeholder={t('general.classes-names')}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.students')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="students"
              type="text"
              closeMenuOnSelect={false}
              component={Select}
              isMulti
              options={students}
              onChange={(values) =>
                communicationFilterChange('students', values)
              }
              placeholder={t('general.students')}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.employees')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="employees"
              type="text"
              closeMenuOnSelect={false}
              component={Select}
              isMulti
              options={employees}
              onChange={(values) =>
                communicationFilterChange('employees', values)
              }
              placeholder={t('general.employees')}
            />
          </div>
        </div>
        <div className="form__form-group">
          <div className="form__form-group-field">
            <span className="generic__label"> {t('general.only-sent')} </span>
            <Field
              name="onlySent"
              component={renderCheckBoxField}
              className="colored-click"
              onChange={(evt) => communicationFilterCheckChange(evt)}
            />
          </div>
          <ButtonToolbar className="filter-button">
            <Button
              color="success"
              onClick={() => filterClick(communicationFilter)}
            >
              {t('general.filter')}
            </Button>
          </ButtonToolbar>
        </div>
      </form>
    </Fragment>
  );
};

Filter.propTypes = {
  t: PropTypes.func.isRequired,
  filterClick: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
  students: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
  classes: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
  lastFilter: PropTypes.shape(PropTypes.shape),
  memoBox: PropTypes.string.isRequired,
};

Filter.defaultProps = {
  lastFilter: null,
};

export default reduxForm({
  form: 'CommunicationFIlter',
})(withTranslation('common')(Filter));
